import { Check, CircleAlert, X } from 'lucide-react';
import { Fragment } from 'react';

import { type ChatAppearance } from '@apps/web/components/phoebe_chat/chat_appearance';
import {
    type ApprovalDockProposal,
    useApprovalDock,
} from '@apps/web/components/phoebe_chat_view/artifacts/use_approval_dock';
import { ReferenceBadge } from '@apps/web/components/phoebe_chat_view/assistant_responses/reference_markdown';
import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import { cn } from '@apps/web/lib/utils';

function ProposalSummary({
    proposal,
}: {
    readonly proposal: ApprovalDockProposal;
}) {
    const entityName = proposal.entity?.name;
    const entityNameIndex = entityName ? proposal.text.indexOf(entityName) : -1;
    const entityBadge = proposal.entity ? (
        <ReferenceBadge
            forceInPanelDetail={true}
            reference={{
                type: proposal.entity.type,
                id: proposal.entity.id,
                display_name: proposal.entity.name,
            }}
        />
    ) : null;

    if (entityNameIndex === -1 || entityName === undefined) {
        return (
            <Fragment>
                {proposal.text}
                {entityBadge && <Fragment> {entityBadge}</Fragment>}
            </Fragment>
        );
    }

    return (
        <Fragment>
            {proposal.text.slice(0, entityNameIndex)}
            {entityBadge}
            {proposal.text.slice(entityNameIndex + entityName.length)}
        </Fragment>
    );
}

/**
 * Staged proposals, docked in their own container directly above the
 * composer instead of painted into the transcript. The transcript stays a
 * record of what was said; anything waiting on the scheduler rises to the
 * composer edge, where the next action happens. Resolving a proposal
 * collapses it into a one-line receipt in place.
 *
 * Mounted with a conversation-scoped key, so switching chats remounts the
 * dock and receipts never leak into another conversation.
 */
export function ApprovalDock({
    appearance = 'default',
}: {
    readonly appearance?: ChatAppearance;
}) {
    const dock = useApprovalDock();

    if (!dock.isVisible) {
        return null;
    }

    return (
        <div className="mb-2 flex flex-col gap-1.5">
            {dock.receipts.map((receipt) => (
                <div
                    key={receipt.itemId}
                    role="status"
                    className={cn(
                        'approval-dock-in flex min-w-0 items-center',
                        appearance === 'v3-agent'
                            ? 'max-w-[700px] gap-2.5 text-sm leading-5 text-[#002429]'
                            : 'border-border/60 bg-muted/20 text-muted-foreground gap-1.5 rounded-lg border px-3 py-1.5 text-xs',
                    )}
                >
                    {receipt.action === 'approve' ? (
                        <Check
                            aria-hidden={true}
                            className={cn(
                                'shrink-0 text-emerald-600',
                                appearance === 'v3-agent'
                                    ? 'size-[18px]'
                                    : 'h-3.5 w-3.5',
                            )}
                        />
                    ) : (
                        <X
                            aria-hidden={true}
                            className={cn(
                                'text-muted-foreground shrink-0',
                                appearance === 'v3-agent'
                                    ? 'size-[18px]'
                                    : 'h-3.5 w-3.5',
                            )}
                        />
                    )}
                    {appearance === 'v3-agent' ? (
                        <span className="min-w-0 truncate text-[#244045]">
                            <span className="font-semibold">
                                {receipt.action === 'approve'
                                    ? 'Approved'
                                    : 'Rejected'}
                            </span>
                            {' — '}
                            {receipt.text}
                        </span>
                    ) : (
                        <span className="min-w-0 truncate">
                            {receipt.action === 'approve'
                                ? 'Approved'
                                : 'Rejected'}
                            {' — '}
                            {receipt.text}
                        </span>
                    )}
                </div>
            ))}
            {dock.open.map((proposal) => {
                const pendingAction =
                    dock.pendingDecision?.itemId === proposal.itemId
                        ? dock.pendingDecision.action
                        : null;
                const isApproving = pendingAction === 'approve';
                const isRejecting = pendingAction === 'reject';
                return (
                    <section
                        key={proposal.itemId}
                        aria-label="Review proposed change"
                        className="approval-dock-in border-border bg-background overflow-hidden rounded-lg border shadow-xs"
                    >
                        <div className="flex min-w-0 gap-2.5 px-3 pt-3 pb-2.5">
                            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400">
                                <CircleAlert
                                    aria-hidden={true}
                                    className="size-3.5"
                                />
                            </span>
                            <div aria-live="polite" className="min-w-0 flex-1">
                                <p className="text-foreground text-xs font-semibold">
                                    Review this change
                                </p>
                                <p className="text-foreground/80 mt-1 min-w-0 text-[13px] leading-5">
                                    <ProposalSummary proposal={proposal} />
                                </p>
                            </div>
                        </div>
                        <div className="border-border/60 bg-muted/20 flex items-center gap-1.5 border-t px-3 py-2">
                            <Button
                                size="sm"
                                className="h-7 px-3 text-xs"
                                aria-label={`Approve: ${proposal.text}`}
                                aria-busy={isApproving}
                                disabled={dock.pendingDecision !== null}
                                onClick={() => {
                                    void dock.resolve(proposal, 'approve');
                                }}
                            >
                                {isApproving ? (
                                    <Spinner size="xs" />
                                ) : (
                                    <Check aria-hidden={true} />
                                )}
                                {isApproving ? 'Approving…' : 'Approve'}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-3 text-xs"
                                aria-label={`Reject: ${proposal.text}`}
                                aria-busy={isRejecting}
                                disabled={dock.pendingDecision !== null}
                                onClick={() => {
                                    void dock.resolve(proposal, 'reject');
                                }}
                            >
                                {isRejecting ? (
                                    <Spinner size="xs" />
                                ) : (
                                    <X aria-hidden={true} />
                                )}
                                {isRejecting ? 'Rejecting…' : 'Reject'}
                            </Button>
                        </div>
                    </section>
                );
            })}
        </div>
    );
}
